import React from 'react';
import { motion } from 'framer-motion';
import { BookOpen, Users, Zap } from 'lucide-react';

const features = [
  {
    icon: BookOpen,
    title: "Research Driven",
    description: "Read insights from credible experts backed by data, not rumors or hearsay.",
    color: "#2563EB",
    delay: 0
  },
  {
    icon: Users,
    title: "Community Intelligence",
    description: "Discuss stocks with serious investors and analysts who share your goals.",
    color: "#22D3EE",
    delay: 0.1
  },
  {
    icon: Zap,
    title: "Fast Insights",
    description: "Get quick stock analysis without spending hours searching through noise.",
    color: "#10B981",
    delay: 0.2
  }
];

const FeaturesSection = () => {
  return (
    <section id="features" className="py-20 md:py-32 relative" data-testid="features-section">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#F8FAFC] mb-4">
            Built for the{' '}
            <span className="gradient-text">New-Age Investor</span>
          </h2>
          <p className="text-[#94A3B8] text-base md:text-lg max-w-2xl mx-auto">
            Everything you need to make informed investment decisions
          </p>
        </motion.div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: feature.delay, duration: 0.5 }}
              whileHover={{ y: -8, transition: { duration: 0.3 } }}
              className="group relative"
              data-testid={`feature-card-${index}`}
            >
              {/* Glow effect on hover */}
              <div 
                className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl"
                style={{ backgroundColor: `${feature.color}20` }}
              />
              
              <div className="relative bg-[#1E293B]/50 backdrop-blur-md border border-[#334155] rounded-2xl p-8 h-full group-hover:border-opacity-50 transition-all duration-300"
                   style={{ '--hover-border': feature.color }}
              >
                {/* Animated Icon */}
                <motion.div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6 relative overflow-hidden"
                  style={{ backgroundColor: `${feature.color}15` }}
                  whileHover={{ scale: 1.1 }}
                  transition={{ type: "spring", stiffness: 400 }}
                >
                  {/* Icon background animation */}
                  <motion.div
                    className="absolute inset-0"
                    style={{ backgroundColor: feature.color }}
                    initial={{ scale: 0, opacity: 0 }}
                    whileHover={{ scale: 1, opacity: 0.2 }}
                    transition={{ duration: 0.3 }}
                  />
                  <feature.icon 
                    size={28} 
                    style={{ color: feature.color }}
                    className="relative z-10"
                  />
                </motion.div>

                <h3 className="text-[#F8FAFC] font-bold text-xl mb-3 group-hover:text-white transition-colors">
                  {feature.title}
                </h3>
                <p className="text-[#94A3B8] text-sm leading-relaxed">
                  {feature.description}
                </p>

                {/* Bottom accent line */}
                <motion.div 
                  className="absolute bottom-0 left-6 right-6 h-[2px] rounded-full"
                  style={{ backgroundColor: feature.color }}
                  initial={{ scaleX: 0 }}
                  whileInView={{ scaleX: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: feature.delay + 0.3, duration: 0.5 }}
                />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
